import React from 'react';
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import { BRAND } from '../theme/fitisticTokens';

/**
 * PageHeader — Clean White header card with Black & Purple theme.
 *
 * Props:
 *   title       {string}   required — page title
 *   subtitle    {string}   optional — short description line
 *   icon        {node}     optional — icon shown in a purple tile left of title
 *   actions     {node}     optional — right-side action buttons / chips
 *   meta        {node}     optional — extra content beside title (count, chips, etc.)
 */
export default function PageHeader({
  title,
  subtitle,
  icon,
  actions,
  meta,
  gradient, // backward compatibility prop (rendered as accent bar if provided)
  sx = {},
}) {
  const accentColor = extractColor(gradient) || BRAND.primary;

  return (
    <Card
      variant="outlined"
      sx={{
        position: 'relative',
        mb: 3,
        borderRadius: '16px',
        bgcolor: '#FFFFFF',
        borderColor: BRAND.divider,
        boxShadow: '0 2px 10px rgba(15,23,42,0.03)',
        overflow: 'hidden',
        animation: 'fitHeaderIn 0.25s ease-out',
        '@keyframes fitHeaderIn': {
          from: { opacity: 0, transform: 'translateY(4px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        ...sx,
      }}
    >
      {/* Sleek purple accent bar along the top */}
      <Box
        sx={{
          height: 3,
          background: `linear-gradient(90deg, ${accentColor}, ${BRAND.primaryLight})`,
          width: '100%',
        }}
      />

      <CardContent sx={{ p: { xs: 2.25, sm: 2.75 }, '&:last-child': { pb: { xs: 2.25, sm: 2.75 } } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Stack direction="row" spacing={1.75} alignItems="center" minWidth={0}>
            {icon && (
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '12px',
                  flexShrink: 0,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: BRAND.primarySoft,
                  border: `1px solid ${BRAND.primarySoftBorder}`,
                  color: BRAND.primary,
                  '& svg': { fontSize: 22, color: BRAND.primary },
                }}
              >
                {icon}
              </Box>
            )}
            <Box minWidth={0}>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                <Typography
                  component="h1"
                  sx={{
                    fontSize: { xs: '1.25rem', sm: '1.45rem' },
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                    color: BRAND.textDark,
                    lineHeight: 1.25,
                  }}
                >
                  {title}
                </Typography>
                {meta}
              </Stack>
              {subtitle && (
                <Typography
                  sx={{
                    mt: 0.35,
                    fontSize: '0.84rem',
                    color: BRAND.textMid,
                    lineHeight: 1.5,
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Stack>

          {actions && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexShrink={0}
              flexWrap="wrap"
              useFlexGap
            >
              {actions}
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

function extractColor(gradient) {
  if (!gradient || typeof gradient !== 'string') return null;
  const match = gradient.match(/#([0-9a-f]{6})/i);
  return match ? `#${match[1]}` : null;
}
